import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  computed,
  inject,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Observable, concatMap, from, toArray } from 'rxjs';
import { SharedModule } from '@shared/shared.module';
import { Permissions } from '@app/core/guard/Permissions';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@app/core/services/helpers/toast-helper.service';
import { Option } from '@interfaces/common/option';
import { StandardAccountDictionaryGovernanceService } from '@app/core/services/http/accounting-data-standardization/standard-account-dictionary-governance.service';
import { StandardAccountDictionaryEntry } from '@interfaces/accounting-data-standardization/responses/standard-account-dictionary.response';
import {
  StandardAccountDictionaryProposal,
  StandardAccountDictionaryProposalStatus,
  StandardAccountDictionaryProposalSummary,
  StandardAccountDictionaryVersion,
  StandardAccountDictionaryVersionDetail,
} from '@interfaces/accounting-data-standardization/responses/standard-account-dictionary-governance.response';
import { StandardAccountDictionaryService } from '@src/app/features/dashboard/accounting-data-standardization/services/standard-account-dictionary.service';
import { describeBackendError } from '@src/app/features/dashboard/accounting-data-standardization/utils/backend-error.util';
import {
  DictionaryReviewGroup,
  proposedItemText,
  revertedVersionNumber,
} from '@src/app/features/dashboard/accounting-data-standardization/utils/standard-account-dictionary-governance.util';
import {
  DictionaryProposalCardComponent,
  DictionaryProposalDocumentRequest,
} from '@src/app/features/dashboard/accounting-data-standardization/components/dictionary-proposal-card/dictionary-proposal-card.component';
import { DictionaryEntriesBrowserComponent } from '@src/app/features/dashboard/accounting-data-standardization/components/dictionary-entries-browser/dictionary-entries-browser.component';
import { DictionaryVersionTimelineComponent } from '@src/app/features/dashboard/accounting-data-standardization/components/dictionary-version-timeline/dictionary-version-timeline.component';
import { DictionaryReasonDialogComponent } from '@src/app/features/dashboard/accounting-data-standardization/components/dictionary-reason-dialog/dictionary-reason-dialog.component';
import { DictionaryProposalApplyDialogComponent } from '@src/app/features/dashboard/accounting-data-standardization/components/dictionary-proposal-apply-dialog/dictionary-proposal-apply-dialog.component';
import { DictionaryEntryEditDialogComponent } from '@src/app/features/dashboard/accounting-data-standardization/components/dictionary-entry-edit-dialog/dictionary-entry-edit-dialog.component';
import { DictionaryReviewListComponent } from '@src/app/features/dashboard/accounting-data-standardization/components/dictionary-review-list/dictionary-review-list.component';
import {
  DictionarySection,
  DictionarySummaryBarComponent,
} from '@src/app/features/dashboard/accounting-data-standardization/components/dictionary-summary-bar/dictionary-summary-bar.component';

/** Filtro de situação da seção Pendentes. */
const PROPOSAL_FILTERS: Option[] = [
  { value: 'Pending', label: 'Pendentes' },
  { value: 'Applied', label: 'Aplicadas' },
  { value: 'Rejected', label: 'Recusadas' },
  { value: 'Reverted', label: 'Desfeitas' },
];

/** Motivo da volta de versão: o `Reason` da versão aceita até 2000. */
const ROLLBACK_REASON_MAX_LENGTH = 2000;

/**
 * Aba "Dicionário" da listagem da Padronização (PBI 3423, DPA-DIC-DB; task 3452), só com
 * `Permissions.AccountingStandardAccountDictionary.LIST`. Indicadores no topo (A revisar, Pendentes, Versão em uso) e
 * quatro seções:
 * - A revisar (padrão): propostas do revisor que já entraram sozinhas no dicionário, em lista compacta por documento,
 *   com Manter (sai da fila) e Desfazer (tira o texto numa versão nova, motivo obrigatório).
 * - Pendentes: o que não entra sozinho (definição nova, falhas), em cartões com Aplicar, Editar e aplicar e Recusar;
 *   filtro de situação.
 * - Verbetes: a versão ativa, com busca, filtro DRE/Balanço e edição manual (motivo obrigatório).
 * - Histórico: linha do tempo com "Voltar para esta versão" (motivo obrigatório).
 * Mudar exige `MANAGE`. Depois de mudar o dicionário, recarrega e limpa o cache usado pelo De/Para.
 */
@Component({
  selector: 'app-standard-account-dictionary-panel',
  standalone: true,
  imports: [
    FormsModule,
    SharedModule,
    DictionarySummaryBarComponent,
    DictionaryReviewListComponent,
    DictionaryProposalCardComponent,
    DictionaryEntriesBrowserComponent,
    DictionaryVersionTimelineComponent,
    DictionaryReasonDialogComponent,
    DictionaryProposalApplyDialogComponent,
    DictionaryEntryEditDialogComponent,
  ],
  templateUrl: './standard-account-dictionary-panel.component.html',
  styleUrl: './standard-account-dictionary-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandardAccountDictionaryPanelComponent {
  private readonly governance = inject(StandardAccountDictionaryGovernanceService);
  private readonly dictionaryCache = inject(StandardAccountDictionaryService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(DictionaryReasonDialogComponent)
  private reasonDialog?: DictionaryReasonDialogComponent;
  @ViewChild(DictionaryProposalApplyDialogComponent)
  private applyDialog?: DictionaryProposalApplyDialogComponent;
  @ViewChild(DictionaryEntryEditDialogComponent)
  private entryEditDialog?: DictionaryEntryEditDialogComponent;

  /** Abrir o documento que motivou uma proposta (a página usa o mesmo caminho do link compartilhável). */
  readonly openDocumentRequested = output<DictionaryProposalDocumentRequest>();
  /** Contagem nova das propostas (a página mostra `toReview + pending` na aba). */
  readonly summaryChanged = output<StandardAccountDictionaryProposalSummary>();

  /** Manter, desfazer, aplicar, recusar, editar e voltar versão só com MANAGE (LIST abre a aba). */
  protected readonly canManage = toSignal(
    inject(PermissionsService).hasPermission(
      Permissions.AccountingStandardAccountDictionary.MANAGE
    ),
    { initialValue: false }
  );

  protected readonly proposalFilters = PROPOSAL_FILTERS;
  protected readonly section = signal<DictionarySection>('review');
  protected readonly proposalFilter = signal<StandardAccountDictionaryProposalStatus>('Pending');

  /** Aplicadas sozinhas ainda não revistas (seção A revisar). */
  protected readonly reviewProposals = signal<StandardAccountDictionaryProposal[]>([]);
  protected readonly reviewLoading = signal(true);
  protected readonly reviewError = signal<string | null>(null);

  /** Seção Pendentes, conforme o filtro de situação. */
  protected readonly proposals = signal<StandardAccountDictionaryProposal[]>([]);
  protected readonly proposalsLoading = signal(true);
  protected readonly proposalsError = signal<string | null>(null);

  /** Contagem dos indicadores. Nula enquanto não carregou. */
  protected readonly summary = signal<StandardAccountDictionaryProposalSummary | null>(null);

  protected readonly versions = signal<StandardAccountDictionaryVersion[]>([]);
  protected readonly versionsLoading = signal(true);
  protected readonly versionsError = signal<string | null>(null);

  protected readonly activeDetail = signal<StandardAccountDictionaryVersionDetail | null>(null);
  protected readonly entriesLoading = signal(true);
  protected readonly entriesError = signal<string | null>(null);

  /** Proposta sendo aplicada, mantida ou desfeita (spinner no cartão ou na linha). */
  protected readonly busyProposalId = signal<string | null>(null);
  /** Grupo em "Manter todas". */
  protected readonly busyGroupKey = signal<string | null>(null);
  /** Alguma mudança em andamento: trava os botões de todas as seções. */
  protected readonly isMutating = signal(false);

  protected readonly activeVersion = computed(
    () => this.versions().find(version => version.status === 'Active') ?? null
  );
  protected readonly activeEntries = computed(() => this.activeDetail()?.entries ?? []);
  protected readonly isLoading = computed(
    () => this.reviewLoading() || this.proposalsLoading() || this.versionsLoading()
  );

  constructor() {
    this.reloadProposals();
    this.loadVersions();
  }

  refresh(): void {
    this.reloadProposals();
    this.loadVersions(true);
  }

  protected setSection(section: DictionarySection): void {
    this.section.set(section);
  }

  protected setProposalFilter(status: string | null): void {
    const next = (status || 'Pending') as StandardAccountDictionaryProposalStatus;
    if (this.proposalFilter() === next) return;
    this.proposalFilter.set(next);
    this.loadProposals();
  }

  /** Recarrega o que depende das propostas: indicadores, A revisar e Pendentes. */
  private reloadProposals(): void {
    this.loadSummary();
    this.loadReview();
    this.loadProposals();
  }

  private loadSummary(): void {
    this.governance
      .getProposalSummary()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: summary => {
          if (!summary) return;
          this.summary.set(summary);
          this.summaryChanged.emit(summary);
        },
        error: () => undefined,
      });
  }

  protected loadReview(): void {
    this.reviewLoading.set(true);
    this.reviewError.set(null);
    this.governance
      .getProposals('Applied', true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: list => {
          this.reviewProposals.set(list ?? []);
          this.reviewLoading.set(false);
        },
        error: (err: unknown) => {
          this.reviewLoading.set(false);
          this.reviewError.set(
            describeBackendError(
              err,
              'Não foi possível carregar as mudanças a revisar. Use Atualizar para tentar de novo.'
            )
          );
        },
      });
  }

  protected loadProposals(): void {
    const status = this.proposalFilter();
    this.proposalsLoading.set(true);
    this.proposalsError.set(null);
    this.governance
      .getProposals(status)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: list => {
          if (status !== this.proposalFilter()) return;
          this.proposals.set(list ?? []);
          this.proposalsLoading.set(false);
        },
        error: (err: unknown) => {
          if (status !== this.proposalFilter()) return;
          this.proposalsLoading.set(false);
          this.proposalsError.set(
            describeBackendError(
              err,
              'Não foi possível carregar as propostas do revisor. Use Atualizar para tentar de novo.'
            )
          );
        },
      });
  }

  /** Recarrega as versões e, se a ativa mudou (ou `force`), os verbetes dela. */
  protected loadVersions(force = false): void {
    this.versionsLoading.set(true);
    this.versionsError.set(null);
    this.governance
      .getVersions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: list => {
          const sorted = [...(list ?? [])].sort((a, b) => b.number - a.number);
          this.versions.set(sorted);
          this.versionsLoading.set(false);
          const active = sorted.find(version => version.status === 'Active');
          if (!active) {
            this.activeDetail.set(null);
            this.entriesLoading.set(false);
            this.entriesError.set(
              'Nenhuma versão do dicionário está em uso. Use Atualizar para conferir de novo.'
            );
            return;
          }
          if (force || this.activeDetail()?.number !== active.number) {
            this.loadActiveEntries(active.number);
          }
        },
        error: (err: unknown) => {
          this.versionsLoading.set(false);
          this.entriesLoading.set(false);
          const message = describeBackendError(
            err,
            'Não foi possível carregar as versões do dicionário. Use Atualizar para tentar de novo.'
          );
          this.versionsError.set(message);
          if (!this.activeDetail()) this.entriesError.set(message);
        },
      });
  }

  private loadActiveEntries(number: number): void {
    this.entriesLoading.set(true);
    this.entriesError.set(null);
    this.governance
      .getVersion(number)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: detail => {
          this.activeDetail.set(detail);
          this.entriesLoading.set(false);
        },
        error: (err: unknown) => {
          this.entriesLoading.set(false);
          this.entriesError.set(
            describeBackendError(
              err,
              'Não foi possível carregar os verbetes da versão em uso. Use Atualizar para tentar de novo.'
            )
          );
        },
      });
  }

  // ---- A revisar: manter e desfazer ----

  protected keepProposal(proposal: StandardAccountDictionaryProposal): void {
    if (!this.canManage() || this.isMutating()) return;
    this.busyProposalId.set(proposal.id);
    this.runMutation(
      this.governance.keepProposal(proposal.id),
      () => 'Mudança mantida: o texto continua no dicionário e saiu da lista A revisar.',
      'Não foi possível manter a mudança. Use Atualizar para conferir se ela ainda está a revisar e tente de novo.',
      false
    );
  }

  protected keepAll(group: DictionaryReviewGroup): void {
    if (!this.canManage() || this.isMutating() || group.proposals.length === 0) return;
    this.busyGroupKey.set(group.key);
    const count = group.proposals.length;
    this.runMutation(
      from(group.proposals).pipe(
        concatMap(proposal => this.governance.keepProposal(proposal.id)),
        toArray()
      ),
      () =>
        count === 1
          ? 'Mudança mantida: o texto continua no dicionário.'
          : `${count} mudanças mantidas: os textos continuam no dicionário.`,
      'Não foi possível manter todas as mudanças deste documento. As que já foram mantidas saíram da lista; use Manter nas que sobraram.',
      false
    );
  }

  protected async revertProposal(proposal: StandardAccountDictionaryProposal): Promise<void> {
    if (!this.canManage() || this.isMutating() || !this.reasonDialog) return;
    const account = proposal.standardAccountLabel ?? 'a conta padrão';
    const note = await this.reasonDialog.open({
      title: 'Desfazer mudança',
      description: `O texto "${proposedItemText(proposal)}" sai do verbete de "${account}" numa versão nova do dicionário, usada no próximo De/Para. A proposta fica em Desfeitas com o seu motivo.`,
      label: 'Motivo',
      placeholder: 'Por que este texto não vale para o plano padrão',
      confirmLabel: 'Desfazer mudança',
      confirmTheme: 'danger',
    });
    if (!note) return;
    this.busyProposalId.set(proposal.id);
    this.runMutation(
      this.governance.revertProposal(proposal.id, { note }),
      result => {
        const number = revertedVersionNumber(result);
        return number
          ? `Mudança desfeita: a versão ${number} do dicionário, sem esse texto, está em uso.`
          : 'Mudança desfeita. O texto já não estava no verbete, então o dicionário não mudou.';
      },
      'Não foi possível desfazer a mudança. Use Atualizar para conferir se ela ainda está no dicionário e tente de novo.'
    );
  }

  // ---- Pendentes: aplicar, editar e aplicar, recusar ----

  protected applyProposal(proposal: StandardAccountDictionaryProposal): void {
    if (!this.canManage() || this.isMutating()) return;
    this.busyProposalId.set(proposal.id);
    this.runMutation(
      this.governance.applyProposal(proposal.id, {}),
      version =>
        `Proposta aplicada: a versão ${version?.number ?? 'nova'} do dicionário está em uso e vale para o próximo De/Para.`,
      'Não foi possível aplicar a proposta. Use Atualizar para conferir se ela ainda está pendente e tente de novo.'
    );
  }

  protected async editAndApplyProposal(proposal: StandardAccountDictionaryProposal): Promise<void> {
    if (!this.canManage() || this.isMutating() || !this.applyDialog) return;
    const body = await this.applyDialog.open(proposal, this.destinationOptions(proposal));
    if (!body) return;
    this.busyProposalId.set(proposal.id);
    this.runMutation(
      this.governance.applyProposal(proposal.id, body),
      version =>
        `Proposta aplicada com o seu texto: a versão ${version?.number ?? 'nova'} do dicionário está em uso.`,
      'Não foi possível aplicar a proposta. Confira o texto e tente de novo.'
    );
  }

  protected async rejectProposal(proposal: StandardAccountDictionaryProposal): Promise<void> {
    if (!this.canManage() || this.isMutating() || !this.reasonDialog) return;
    const note = await this.reasonDialog.open({
      title: 'Recusar proposta',
      description: `A proposta para "${proposal.standardAccountLabel ?? 'a conta padrão'}" sai da fila e fica em Recusadas com a sua nota. O dicionário não muda.`,
      label: 'Nota da recusa',
      placeholder: 'Por que a proposta não vale para o plano padrão',
      confirmLabel: 'Recusar proposta',
      confirmTheme: 'danger',
    });
    if (!note) return;
    this.runMutation(
      this.governance.rejectProposal(proposal.id, { note }),
      () => 'Proposta recusada e registrada com a sua nota.',
      'Não foi possível recusar a proposta. Use Atualizar para conferir se ela ainda está pendente e tente de novo.',
      false
    );
  }

  // ---- Verbetes e versões ----

  protected async editEntry(entry: StandardAccountDictionaryEntry): Promise<void> {
    if (!this.canManage() || this.isMutating() || !this.entryEditDialog) return;
    const body = await this.entryEditDialog.open(entry);
    if (!body) return;
    this.runMutation(
      this.governance.updateEntry(body),
      version =>
        `Verbete "${entry.label}" salvo: a versão ${version?.number ?? 'nova'} do dicionário está em uso.`,
      'Não foi possível salvar o verbete. Confira os campos e tente de novo.'
    );
  }

  protected async rollbackTo(version: StandardAccountDictionaryVersion): Promise<void> {
    if (!this.canManage() || this.isMutating() || !this.reasonDialog) return;
    const reason = await this.reasonDialog.open({
      title: `Voltar para a versão ${version.number}`,
      description: `Cria uma versão nova com o conteúdo da versão ${version.number} e passa a usá-la no De/Para. As versões de depois continuam no histórico.`,
      label: 'Motivo da volta',
      placeholder: 'Por que voltar para esta versão',
      confirmLabel: 'Voltar para esta versão',
      maxLength: ROLLBACK_REASON_MAX_LENGTH,
    });
    if (!reason) return;
    this.runMutation(
      this.governance.activateVersion(version.number, { reason }),
      created =>
        `Pronto: a versão ${created?.number ?? 'nova'}, cópia da versão ${version.number}, está em uso.`,
      'Não foi possível voltar para esta versão. Use Atualizar e tente de novo.'
    );
  }

  /** Contas do mesmo demonstrativo na versão em uso, para o destino de "NÃO entra". */
  private destinationOptions(proposal: StandardAccountDictionaryProposal): Option[] {
    return this.activeEntries()
      .filter(entry => entry.statementType === proposal.statementType && !!entry.standardAccountId)
      .map(entry => {
        const label = entry.path?.trim() || entry.label;
        return { value: entry.standardAccountId as string, label, data: label };
      });
  }

  /**
   * Roda uma mudança: trava os botões, mostra o toast e recarrega as propostas (também no erro, que pode ter sido
   * parcial no "Manter todas"). `changesDictionary` (padrão) limpa o cache do dicionário usado pelo De/Para e recarrega
   * versões e verbetes.
   */
  private runMutation<T>(
    request: Observable<T | null>,
    successMessage: (result: T | null) => string,
    fallbackError: string,
    changesDictionary = true
  ): void {
    this.isMutating.set(true);
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: result => {
        this.finishMutation();
        this.toast.showToastSuccess(successMessage(result));
        if (changesDictionary) {
          this.dictionaryCache.clear();
          this.loadVersions(true);
        }
        this.reloadProposals();
      },
      error: (err: unknown) => {
        this.finishMutation();
        this.toast.showToastError(describeBackendError(err, fallbackError));
        this.reloadProposals();
      },
    });
  }

  private finishMutation(): void {
    this.isMutating.set(false);
    this.busyProposalId.set(null);
    this.busyGroupKey.set(null);
  }
}
